// src/components/desk/MeetingsFeed.jsx — the Meetings exclusive feed + MeetingCard,
// extracted VERBATIM from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition,
// behavior-preserving). Status maps exported: the sidebar Category→Meeting subtree
// and the meetings CSV export in IntakeTab consume them.

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getDistricts, getListingTermsBatch, getMeetingVocab, updateMeeting } from '../../db/core.js'
import { t, tLog, tOr } from '../../lib/i18n.js'
import { Icon, ROLE_ICON } from '../Icon.jsx'
import { getMeetingNudges } from '../../db/core.js'
import { card, Badge, pill, popoverPos, useCloseOnPageMove } from './primitives.jsx'
import { ListingGallery } from './ListingGallery.jsx'
import { HistoryGroup, StateRow } from './HistoryCards.jsx'
import { MeetingCountdown, meetingScheduledAt } from './MeetingCountdown.jsx'
import { ContactPill } from './ContactPill.jsx'
import { meetingContactRows, meetingPropertyId } from '../../lib/meetingContacts.js'
import { meetingNeedsOutcome } from '../../lib/meetingOutcome.js'
import { ReasonChips, REASON_REQUIRED } from './ReasonChips.jsx'
import { fmtTbilisi, tbilisiParts } from '../../lib/tbilisiTime.js'
import { bestCooperation, coopTermsPlain, participationOf } from '../../lib/coopMath.js'
import { MEETING_ACTIVE, MEETING_FINISHED } from '../../../core/meeting-statuses.mjs'
import { fmtPhone } from '../../lib/phone.js'

// ─── Meetings feed (type filter = "Meeting") ──────────────────────────────────
// A meeting (scheduler `viewings` table) is the ONE object that ties a client to a property.
// Its own card only exists in THIS view — everywhere else meetings are audit lines. The card
// is purpose-ordered (client → property → meeting → notes), not table-ordered, because agents
// think "who is seeing what", not "which viewings row". Property is the primary link
// (core_property_id, snapshotted at creation); the listing/source ad is a secondary reference
// that may expire. Everything the card shows already lives on the viewing row + its
// property_snapshot — no core round-trip; the thumbnail is best-effort from listings already
// loaded on the Desk, falling back to ListingGallery's own map slide.
export const MEETING_STATUS_KEYS = {
  scheduled: 'desk_meeting_status_scheduled',
  pending:   'desk_meeting_status_pending',
  binder:    'desk_meeting_status_binder',
  closed:    'desk_meeting_status_closed',
  failed:    'desk_meeting_status_failed',
  cancelled: 'desk_meeting_status_cancelled',
  // Present so the registry is COMPLETE, not because the Desk writes it: the sidebar's
  // status subtree renders t(MEETING_STATUS_KEYS[s]) for every member of MEETING_ACTIVE,
  // so a value listed there and missing here prints an empty row. The endpoint accepts
  // 'rescheduled' for callers that mean it (0392), so it must have a name.
  // ⚠️ A DIFFERENT key from the modal's chip. There the word is an ACTION ("Postpone…",
  // it opens the date picker); here it names a STATE a row is in. One string cannot be
  // both without lying in one of the two places.
  rescheduled: 'desk_meeting_state_rescheduled',
}
// Statuses a person may SELECT as a filter. `rescheduled` is deliberately absent: the Desk
// never writes it (the გადადება chip moves the date and leaves the status alone), so the
// bucket cannot fill — measured 0 rows in the whole table since the value shipped 0392,
// against 10 recorded reschedule EVENTS. An agent who clicked it got an empty feed and no
// explanation, which is the "filter says nothing happened" failure this file warns about
// elsewhere.
// ⚠️ It stays inside MEETING_ACTIVE on purpose. That pair must PARTITION every stored
// status or a row carrying one disappears from every view (it has happened once). This
// only stops the Desk offering a bucket it cannot fill; a row arriving from any other
// caller still shows up under Active.
export const MEETING_ACTIVE_SELECTABLE = MEETING_ACTIVE.filter(s => s !== 'rescheduled')
const MEETING_STATUS = {
  scheduled: { variant: 'blue' },
  pending:   { variant: 'yellow' },
  binder:    { variant: 'amber' },
  closed:    { variant: 'green' },
  failed:    { variant: 'red' },
  // Grey ('default' — Badge has no 'gray', and naming one it does not know would read as
  // a typo rather than a choice). Cancelled is not a bad outcome, it is a NON-outcome: the
  // hour was given back. Red stays reserved for a viewing that happened and produced nothing.
  cancelled: { variant: 'default' },
  rescheduled: { variant: 'default' },
}
// UI aggregates over the five stored statuses — agents live in Active ~95% of the time, so it's
// the default. Internally the statuses stay individual (no meeting_type / no status collapse in
// the data): this is purely how the filter groups them.
// ⚠️ THESE TWO LISTS MUST PARTITION EVERY STORED STATUS. They are not "the interesting
// ones" — a status in neither is in NO view: the Active filter (the default, where agents
// live ~95% of the time) drops it and the Finished filter never picks it up, so the meeting
// silently ceases to exist for its own agent. That happened on 2026-08-03: 'rescheduled'
// shipped as a sixth status and the first meeting marked with it vanished from the Desk.
//
// The rule used to be this comment asking the next person to remember. It is a TEST now
// (`core/meeting-statuses.test.mjs`), which is why the lists moved to a plain .mjs the test
// runner can import; they are re-exported here so every existing importer is untouched.
//
// ⚠️ IMPORTED **AND** RE-EXPORTED, deliberately, and not `export { … } from`. That form
// re-exports the names without binding them in THIS module's scope — and this file uses
// MEETING_ACTIVE itself, in the status filter. Written that way it built green, passed 561
// tests, and threw `MEETING_ACTIVE is not defined` the instant the feed rendered. Caught in
// the harness within a minute of the change; nothing in the gate can see it.
export { MEETING_ACTIVE, MEETING_FINISHED }
const DEAL_LABEL_KEYS = { trade: 'desk_meeting_deal_sale', lease: 'desk_meeting_deal_rent' }

// ── MeetingStrip — THE shared meeting strip (A-lite ruling, designer 2026-07-24) ──
// One contract, two surfaces: the Meetings feed renders it under the pattern-A listing
// card (status · client · time · agents), the home cockpit renders it under the compact
// MeetingCard (agents · property one-liner). `show` gates content to ONLY what that
// surface's face doesn't already display — the strip must never duplicate face facts
// (designer detail, recorded in QUESTIONS.md). Any strip improvement lands here once
// and reaches both surfaces; do NOT re-inline it in either consumer.
// Deal terms on the meeting FACE (owner 2026-08-01). Two states, and the empty one is
// the point: an unrecorded commission is the single most expensive blank on a meeting,
// so the card NAGS instead of showing a dash. Tapping anywhere opens the meeting, where
// the fill door lives — the nudge deliberately opens no door of its own, so there is one
// place terms are edited.
//
// ⚠️ `coops === undefined` (not fetched yet) must render NOTHING. Nudging on absence of
// data rather than absence of terms would put an amber "fill this in" on every card for
// the first second of every page — the fastest way to teach agents to ignore it.
function TermsToken({ coops, partyIds }) {
  if (coops === undefined) return null
  const live = (coops || []).filter(c => c.status !== 'rejected' && c.status !== 'ended')
  const best = bestCooperation(live)
  // ⚠️ The terms shown belong to the AGENTS ON THIS MEETING, not to the cooperation's
  // parent row — that one is rolled up from whichever participant confirmed last, so a
  // colleague's number would read as this meeting's (owner-caught 2026-08-01). A legacy
  // scheduler row carries no party ids at all; there we cannot tell whose terms these are,
  // so the parent stands rather than a nag we cannot justify.
  const mine = partyIds?.length ? participationOf(best, partyIds) : best
  const { total, commission } = coopTermsPlain(
    mine && mine !== best ? { ...mine, visibility: best?.visibility } : mine)
  const text = [total, commission].filter(Boolean).join('/')
  if (text) return <span className="whitespace-nowrap"><Icon name="handshake" size={12} /> {text}</span>
  // ⚠️ NO EXEMPTION FOR A GATED VIEWER (owner 2026-08-01). This used to `return null` when
  // can_see_numbers was false — "terms exist, just not earned, so not a gap" — and that
  // silently removed the amber warning from the face of exactly the meetings that needed it.
  // The gate only ever hides OTHER agents' figures; your own are always yours. So a blank
  // here means THIS meeting's agent has recorded nothing, gate or no gate. That is a gap,
  // and it is theirs to close.
  return (
    <span className="whitespace-nowrap text-amber-600 dark:text-amber-400 font-medium">
      <Icon name="alert-circle" size={12} /> {t('desk_meeting_terms_missing')}
    </span>
  )
}

// ── MeetingStateBlock — the meeting's own answer to "what is the state of this" ──────────
// (2026-08-05, owner: *"does meeting cards display info show what meeting needs most? why do
// we need leads hunting details? we need address price cooperation details client details
// restrictions and anything related to meeting"*.)
//
// The meetings feed renders the same card every other surface does (card-morph ruling A,
// 2026-07-24 — explicitly the "zero-migration baseline", with "C = morph-slot hybrid stays the
// eventual target"). So when the aligned state column shipped 2026-08-04 for listing triage,
// meetings inherited it without anyone choosing: `მესაკუთრის განც.` is which portals the owner
// advertises on, `ვნახეთ` is our crawl date, `შემოწმდა` is the re-checker's debt. Leads-hunting
// facts, on the card of someone standing outside a building at 3:30. This is that morph slot,
// filled for one category, in the ROW ORDER THE OWNER GAVE.
//
// ⚠️ TWO ROW SHAPES REACH THIS, and the difference is not cosmetic (see getMeetingsFeed):
//   • legacy scheduler `viewings` — carry client_name/client_phone, and have NO attendance
//     concept at all, so "who is going" is UNKNOWN there, not empty.
//   • core meetings — carry `attendees[]` with names resolved server-side, and carry NO client
//     name or phone BY DESIGN ("a core meeting owns relationships, not contacts" — db/core.js
//     says outright not to "fix" this by joining a name in). They show the client's
//     REPRESENTATIVE, which is who you actually coordinate with.
// A row that cannot be answered VANISHES (the column's zero-rows rule) — it never renders a
// dash, because on this card a dash would read as "nobody is going".
//
// ⚠️ INVOLVED ≠ ATTENDING, and mixing them pays a guest (B292, `0397` + `0404`). `agent_ids`
// is the INVOLVED set — what commission and ranking read. `attendees[]` is everyone on the
// meeting, including `involved:false` guests who are going and take no share. `attending` is
// TRI-STATE: null = nobody answered, true = going, false = involved but not going. So the
// "going" row is only drawn when someone actually answered.
// The restrictions row's FOUR states. It is the only row here that always renders, and that
// is the whole point of B312: on every other row an absent line means "nothing to say", but
// "we never asked whether this flat allows pets" is not nothing — it is the thing that turns
// someone away at the door, and it is an instruction to ask before going.
//   subjects  → the recorded stances, worded from the feature vocabulary
//   cleared   → someone ASKED and there are none (B237's third state, a positive statement)
//   hidden    → someone answered and THIS VIEWER has not earned the value. ⚠️ hidden ≠ absent;
//               B237 exists because rendering it as "unasked" is the confusion to avoid.
//   unknown   → nobody has asked. Muted, and deliberately not amber: it is a gap in our
//               knowledge, not a warning about the flat.
function RestrictionsValue({ restr, clear, features }) {
  const subjects = restr?.subjects || []
  if (subjects.length) {
    const words = subjects.map(s => {
      const f = (features || []).find(x => x.code === s.code)
      return f?.name_ka || f?.name_en || s.code.replace(/^pol_/, '').replace(/_/g, ' ')
    })
    return (
      <span className="text-amber-600 dark:text-amber-400 truncate inline-block max-w-full align-bottom"
        title={words.join(', ')}>{words.join(', ')}</span>
    )
  }
  if (clear?.hidden) return <span className="text-muted-foreground">{t('desk_meeting_restr_hidden')}</span>
  if (clear?.cleared) return <span className="text-muted-foreground">{t('desk_meeting_restr_none')}</span>
  return <span className="text-muted-foreground/70">{t('desk_meeting_restr_unknown')}</span>
}

// The visit/purpose marker, ONE implementation for the two surfaces that draw it (the home
// cockpit strip and the meetings-feed state column). Only three purposes earn card space —
// they are the ones that change what a manager does — and a repeat shows its ordinal when
// the feed could compute one, its plain label when it could not. Extracted 2026-08-15: the
// strip had it inline and the state column had nothing, which is how a fact computed for
// every row in getMeetingsFeed ended up rendered on the one surface it was NOT designed for.
export function purposeMarkOf(v) {
  if (!v?.purpose_code) return null
  if (v.purpose_code === 'repeat_viewing') {
    return v.__visit_no >= 2 ? tLog('desk_meeting_visit_n', { n: v.__visit_no }) : t('desk_meeting_purpose_repeat_viewing')
  }
  // ⚠️ THE ORDINAL OUTRANKS THE STORED MARKER (2026-08-15). `first_viewing` was printed
  // verbatim even on a row the feed had already numbered as the 3rd visit — the card
  // restating a stale tap as fact while carrying the evidence against it. The default that
  // produced those rows is fixed at the create form, but the rows already exist, so the face
  // reports what the history says and lets the stored code speak only when nothing contradicts
  // it. (Note the repeat branch above has always consulted __visit_no; only this one did not.)
  if (v.purpose_code === 'first_viewing') {
    return v.__visit_no >= 2 ? tLog('desk_meeting_visit_n', { n: v.__visit_no }) : t('desk_meeting_purpose_first_viewing')
  }
  if (v.purpose_code === 'signing') return t('desk_meeting_purpose_signing')
  return null
}

// ── WHAT A CLOSED DEAL CLOSED ON (owner 2026-08-26) ──────────────────────────────────────
// Only figures the CLOSE itself wrote (see the server's own note): the price and the fee, and
// the agreed rate from the same row when it explains the fee. Terms recorded on that flat
// afterwards belong to a different cooperation and must never re-label a closed deal.
// Returns a string, or null when the deal closed with no figures — which the caller states
// out loud rather than falling back to the "fill these in" nag.
export function closingTermsText(deal) {
  const ct = deal?.closing_terms
  if (!ct) return null
  const money = (n, cur) => n == null ? null : `${Number(n).toLocaleString('en-US')}${cur ? ' ' + cur : ''}`
  const price = money(ct.final_price, ct.final_currency)
  const fee = money(ct.final_commission_amount, ct.final_currency)
  const rate = ct.agreed_commission_percent != null ? `${ct.agreed_commission_percent}%` : null
  const feePart = fee ? `${t('desk_deal_closed_commission')} ${fee}${rate ? ` (${rate})` : ''}` : (rate || null)
  return [price, feePart].filter(Boolean).join(' · ') || null
}

// ── The meeting's contact pill (owner, 2026-09-06) ──────────────────────────────────────
// THE property card's ContactPill, not a new control: same popover, same call/WhatsApp/copy
// rows, same add-contact door onto the meeting's property. What differs is only the list it
// opens on — the people INVOLVED IN THIS MEETING first (lib/meetingContacts.js decides who),
// then the property's own contacts, fetched on open exactly as the card footer does. Rendered
// on both meeting surfaces from this one place, so they cannot drift.
export function MeetingContactPill({ v, agents = [] }) {
  const propertyId = meetingPropertyId(v)
  const rows = meetingContactRows(v, agents)
  if (!rows.length && !propertyId) return null
  const address = v.property_snapshot?.address || undefined
  // Every meeting must have a CLIENT and an OWNER party (owner, 2026-09-06). A core meeting's
  // client is a party behind the access gate — present, just not listed — so it is vouched for;
  // a legacy row's client is the name/phone on the row, which becomes a client row above. The
  // owner is judged against the property's contacts once the pill has read them. The face goes
  // dashed (the "add" style every card uses for an absent number) when the client is missing,
  // so the gap is visible before anyone opens the popover.
  const clientKnown = !!(v.client_party_id || rows.some(r => r.role === 'client'))
  return (
    <ContactPill chip iconOnly extraContacts={rows} hideOtherAgents
      requireRoles={['client', 'owner']} satisfiedRoles={clientKnown ? ['client'] : []}
      known={clientKnown ? null : false}
      listing={propertyId ? { property_id: propertyId, address } : undefined}
      propertyId={propertyId || undefined} address={address}
      label={t('meeting_contacts_pill')} />
  )
}

// ── The outcome pill (owner, 2026-09-06: pill #4 — "for a meeting whose time has passed, a
// pill that records the result and reason") ───────────────────────────────────────────────
// Shown only while a meeting OWES an outcome (lib/meetingOutcome.js: core row, hour passed,
// still scheduled). It is the modal's outcome list, copied not re-invented: the same seven
// statuses in the same order with the same dots, the same ReasonChips under the pressed row,
// the same rule that failed/cancelled must say why, the same PATCH. What it does NOT copy is
// the money and the calendar: closed, binder and rescheduled all need more than a tap, so
// those rows open the meeting armed on that question (the nudge deep-links, reused) instead
// of half-asking it here.
// status-subset: every status EXCEPT `scheduled` — that is the state being moved FROM, not an
// outcome you can tap. Deliberately six, and the comment above says what each row does.
const OUTCOME_ROWS = ['pending', 'failed', 'cancelled', 'closed', 'binder', 'rescheduled']
const OUTCOME_DOT = {
  pending: 'bg-yellow-500', binder: 'bg-amber-500', closed: 'bg-emerald-500',
  failed: 'bg-red-500', cancelled: 'bg-muted-foreground',
  rescheduled: 'border border-dashed border-muted-foreground',
}
export function MeetingOutcomePill({ v, onOpen, onChanged, vocab: preloadedVocab = null }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const [vocab, setVocab] = useState(preloadedVocab)
  const [status, setStatus] = useState('')
  const [reason, setReason] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  useCloseOnPageMove(open, () => setOpen(false), panelRef)
  useEffect(() => {
    if (!open) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [open])
  if (!meetingNeedsOutcome(v)) return null
  const toggle = e => {
    e.stopPropagation()
    setOpen(o => {
      if (!o) {
        setPos(popoverPos(btnRef.current, 320))
        if (!vocab) getMeetingVocab().then(setVocab).catch(() => setVocab({ reasons: [] }))
      }
      return !o
    })
  }
  const pick = s => {
    // The three rows that need more than a reason hand over to the modal, ARMED on their
    // own question — the same deep-link shape the nudge banner uses (armFromNudge).
    if (s === 'closed')      { setOpen(false); onOpen?.({ ...v, __askOutcome: true }); return }
    if (s === 'binder')      { setOpen(false); onOpen?.({ ...v, __askBinder: true }); return }
    if (s === 'rescheduled') { setOpen(false); onOpen?.({ ...v, __askReschedule: true }); return }
    setStatus(cur => (cur === s ? '' : s)); setReason(null); setErr('')
  }
  const canSave = !!status && (!REASON_REQUIRED.has(status) || !!reason)
  async function save() {
    if (!canSave || busy) return
    setBusy(true); setErr('')
    try {
      // The reason rides the SAME PATCH as the status (0580's rule, kept): a status can never
      // land while the sentence explaining it fails.
      await updateMeeting(v.core_meeting_id, { status, ...(reason ? { status_reason: reason } : {}) })
      setOpen(false); setStatus(''); setReason(null)
      onChanged?.()
    } catch (e) { setErr(e.message || String(e)) } finally { setBusy(false) }
  }
  return (
    <span className="inline-block" onClick={e => e.stopPropagation()}>
      <button ref={btnRef} type="button" onClick={toggle} title={t('desk_meeting_outcome_title')}
        className={pill('warn', { door: true, extra: 'bg-background' })}>
        <Icon name="flag" size={14} /> <span>{t('desk_meeting_outcome_pill')}</span>
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} className="fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 w-[320px] text-sm overflow-y-auto overscroll-contain" style={pos}
          onClick={e => e.stopPropagation()}>
          <div className="px-3 pb-1 text-xs font-semibold text-muted-foreground">{t('desk_meeting_outcome_title')}</div>
          {OUTCOME_ROWS.map(s => (
            <div key={s}>
              <button type="button" disabled={busy} onClick={() => pick(s)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm min-h-[40px] disabled:opacity-40 transition-colors ${
                  status === s ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-accent'}`}>
                <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${OUTCOME_DOT[s] || 'bg-muted-foreground'}`} />
                <span className="min-w-0 flex-1 truncate">{t(`desk_meeting_status_${s}`)}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{status === s ? '▲' : '▼'}</span>
              </button>
              {/* The panel renders under ITS OWN row (the modal's standing rule), so the answer
                  appears where the question was pressed. */}
              {status === s && (
                <div className="px-3 pb-2 border-b border-border/40">
                  {vocab === null ? <p className="text-xs text-muted-foreground py-1">{t('desk_loading')}</p>
                    : <ReasonChips vocab={vocab} status={s} value={reason} onPick={setReason} />}
                  {REASON_REQUIRED.has(s) && !reason && (
                    <p className="text-[11px] text-muted-foreground pt-1">{t('desk_meeting_outcome_reason_required')}</p>
                  )}
                  {err && <p className="text-xs text-red-500 pt-1">{err}</p>}
                  <div className="flex justify-end gap-2 pt-1.5">
                    <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setStatus(''); setReason(null) }}>{t('desk_client_cancel')}</button>
                    <button type="button" className="text-xs text-primary font-medium disabled:opacity-50" disabled={!canSave || busy} onClick={save}>
                      {busy ? '…' : t('save')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </span>
  )
}

// ── The meeting card's rail: its pills, bottom row, like every listing card (owner 2026-09-06:
// "put these pills below, like those cards"). One node for both surfaces — the feed hands it
// to HistoryGroup as `strip` (the rail's start slot), the cockpit card draws it under its strip.
export function MeetingRail({ v, agents, onOpen, onChanged }) {
  return (
    <span className="inline-flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <MeetingContactPill v={v} agents={agents} />
      <MeetingOutcomePill v={v} onOpen={onOpen} onChanged={onChanged} />
    </span>
  )
}

function MeetingStateBlock({ v, agents, coops, restr, clear, features, nudge }) {
  const st = MEETING_STATUS[v.status] || { variant: 'default' }
  const stLabel = MEETING_STATUS_KEYS[v.status] ? t(MEETING_STATUS_KEYS[v.status]) : v.status
  const schedAt = meetingScheduledAt(v)
  const nameOfAgent = id => agents.find(a => a.id === id)?.name
  const attendees = v.attendees || []
  // INVOLVED: the deal's agents. Legacy rows only have agent_ids; core rows have both, and the
  // server deliberately mirrors agent_ids as involved-only so one renderer draws both shapes.
  const involved = attendees.length
    ? attendees.filter(a => a.involved !== false).map(a => a.name || nameOfAgent(a.agent_id)).filter(Boolean)
    : (v.agent_ids || []).map(nameOfAgent).filter(Boolean)
  // GOING: only those who ANSWERED yes. Unstated is not a no — see the tri-state note above.
  const going = attendees.filter(a => a.attending === true)
    .map(a => a.name || nameOfAgent(a.agent_id)).filter(Boolean)
  // The client, per shape. A core meeting's representative is labelled as such rather than
  // dressed up as the client — they are different people and the card must not blur them.
  const clientName = v.client_name || v.client_phone || null
  const clientRep = v.__core ? v.client_representative?.name : null
  const partyIds = [v.property_representative?.party_id, ...(v.attendee_party_ids || []), v.client_representative?.party_id].filter(Boolean)
  const terms = (() => {
    if (coops === undefined) return undefined     // not fetched — say nothing, never nag
    const live = (coops || []).filter(c => c.status !== 'rejected' && c.status !== 'ended')
    const best = bestCooperation(live)
    const mine = partyIds.length ? participationOf(best, partyIds) : best
    const { total, commission } = coopTermsPlain(mine && mine !== best ? { ...mine, visibility: best?.visibility } : mine)
    return [commission, total].filter(Boolean).join(' · ') || null
  })()
  return (
    <div className="flex flex-col min-w-0">
      {/* The MEETING's status, which is the status this card is about. The listing's own
          status dot left with StateBlock — a green აქტიური next to ჩანიშნული was two
          different objects' states on one face, and only one of them is the subject. */}
      <div className="pb-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <Badge label={stLabel} variant={st.variant} />
        {/* WHY this meeting exists, and which visit it is. Computed for every row by
            getMeetingsFeed (server `visit_no`, local fallback) and until 2026-08-15 rendered
            only on the home cockpit — so the surface built for the agent standing outside the
            building was the one surface that did not say "3rd visit". */}
        {purposeMarkOf(v) && (
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            <Icon name="target" size={11} /> {purposeMarkOf(v)}
          </span>
        )}
      </div>
      {/* The REASON behind a final status, and the follow-up a pending one is waiting on.
          Both are stored, both were modal-only: a manager scanning the feed could see that
          eleven meetings failed and not one word of why without opening all eleven. */}
      {v.status_reason && (
        <div className="pb-1 text-[11px] text-muted-foreground">
          {tOr(`desk_meeting_reason_${v.status_reason}`, v.status_reason)}
        </div>
      )}
      {/* THE DEAL (0690): how many visits this card stands for, and WHO IS PAID from it —
          the representatives of either side and those who went, never whoever clicked
          close. Names only; the money stays behind the modal's own gates. */}
      {v.deal && v.deal.last_meeting_id && v.deal.last_meeting_id !== v.core_meeting_id && (
        <div className="pb-1 text-[11px] text-muted-foreground"><Icon name="repeat" size={11} /> {t('listings_meeting_revisited')}</div>
      )}
      {v.deal && (!v.deal.last_meeting_id || v.deal.last_meeting_id === v.core_meeting_id) && (
        <div className="pb-1 text-[11px]">
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
            <Icon name="trophy" size={11} /> {tLog('desk_deal_visits', { n: v.deal.visit_count || v.__deal_visits || 1 })}
          </span>
          {v.deal.participants?.length > 0 && (
            <span className="text-muted-foreground"> · {t('desk_deal_participants')}: {v.deal.participants.map(p => p.name).filter(Boolean).join(', ')}</span>
          )}
        </div>
      )}
      {/* THE CHAIN (0690): a visit that follows another says so — "from the previous visit"
          is a record now, not an inference from client+flat. */}
      {v.superseded_by && !v.deal && (
        <div className="pb-1 text-[11px] text-muted-foreground"><Icon name="repeat" size={11} /> {t('meeting_superseded_title')}</div>
      )}
      {v.previous_meeting_id && !v.deal && (
        <div className="pb-1 text-[11px] text-muted-foreground">← {t('desk_meeting_follows_previous')}</div>
      )}
      {v.next_action_date && !MEETING_FINISHED.includes(v.status) && (
        <div className="pb-1 text-[11px] text-muted-foreground">
          <Icon name="calendar" size={11} /> {t('meeting_next_action_label')} {fmtDay(v.next_action_date)}
        </div>
      )}
      {/* What the status does not say (medium nudge, owner-ruled onto the card 2026-08-06):
          the badge names the state, this names the gap inside it. Directly under the badge
          because it is a fact ABOUT that badge, and clicking the card lands on the question. */}
      {nudge && (
        <div className={`pb-1 flex items-start gap-1.5 text-[11px] ${NUDGE_TIER_STYLE.medium.text}`}>
          <span className={`shrink-0 ${NUDGE_TIER_STYLE.medium.dot}`}>●</span>
          <span className="min-w-0">{tOr(`desk_nudge_why_${nudge.why}`, nudge.why)}</span>
        </div>
      )}
      {/* Directly under the status, because ბე IS the status and this is what it means. */}
      <BinderLine v={v} className="pb-1" />
      {/* 1 — the figures you are walking in to negotiate. `undefined` = not fetched yet and
          renders nothing; a fetched-but-empty cooperation is a real gap and says so. */}
      {/* A CLOSED DEAL ANSWERS FOR ITSELF (owner 2026-08-26). The nag below is for a deal
          still being worked; over a closed one it asked an agent to fill in what the close
          had just recorded — and the answer was invisible only because closing ENDS the
          cooperation the live-only filter reads. A deal closed with no figures says that,
          which is a different and true statement. */}
      {v.deal ? (
        <StateRow label={t('desk_meeting_row_terms')}>
          {closingTermsText(v.deal)
            ? <span className="text-emerald-600 dark:text-emerald-400">{closingTermsText(v.deal)}</span>
            : <span className="text-muted-foreground">{t('desk_deal_closed_no_figures')}</span>}
        </StateRow>
      ) : terms !== undefined && (
        <StateRow label={t('desk_meeting_row_terms')}>
          {terms || <span className="text-amber-600 dark:text-amber-400">{t('desk_meeting_terms_missing')}</span>}
        </StateRow>
      )}
      {involved.length > 0 && (
        <StateRow label={t('desk_meeting_row_involved')} title={involved.join(', ')} wrap>
          <span className="truncate inline-block max-w-full align-bottom">{involved.join(', ')}</span>
        </StateRow>
      )}
      {going.length > 0 && (
        <StateRow label={t('desk_meeting_row_going')} title={going.join(', ')} wrap>
          <span className="truncate inline-block max-w-full align-bottom">{going.join(', ')}</span>
        </StateRow>
      )}
      {/* WHO CREATED the record (owner 2026-08-26) — an act, stated as itself. It sits after
          the people IN the deal so "Gigi created it" can never read as "Gigi is in it". */}
      {v.created_by?.name && (
        <StateRow label={t('desk_meeting_row_created_by')} title={v.created_by.name} wrap>
          <span className="truncate inline-block max-w-full align-bottom text-muted-foreground">{v.created_by.name}</span>
        </StateRow>
      )}
      {(clientName || clientRep) && (
        <StateRow label={t(clientName ? 'desk_meeting_row_client' : 'desk_meeting_row_client_rep')}
          title={[v.client_name, v.client_phone].filter(Boolean).join(' · ') || clientRep} wrap>
          <span className="truncate inline-block max-w-full align-bottom">{clientName || clientRep}</span>
        </StateRow>
      )}
      {/* Everyone on this meeting, one tap from a call. A row of its own rather than a chip on
          the status line: this column is the meeting's answer sheet, and "who do I phone" is
          one of its questions. */}
      {schedAt && (
        <StateRow label={t('desk_meeting_row_when')} title={fmtTbilisi(schedAt)} wrap>
          {/* ⚠️ WRAPS, and must. StateRow's value is `whitespace-nowrap` — right for a row of
              platform dots, wrong here: the date plus the countdown measured 201px on one
              line inside a ~162px compact column and hung 26px past the card edge (harness,
              2026-08-05). Wrapping is not truncating, so the rule that the VALUE never loses
              information still holds; it just uses two lines when it needs them. */}
          <span className="inline-flex flex-wrap justify-end items-center gap-x-1.5 whitespace-normal">
            <span className="whitespace-nowrap">
              {fmtTbilisi(schedAt, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
            <MeetingCountdown at={schedAt} status={v.status} />
          </span>
        </StateRow>
      )}
      {/* 6 — the thing that turns you away at the door. ALWAYS rendered, unlike every other
          row here: see RestrictionsValue for why an absent line would be the wrong answer. */}
      <StateRow label={t('desk_meeting_row_restrictions')} wrap>
        <RestrictionsValue restr={restr} clear={clear} features={features} />
      </StateRow>
    </div>
  )
}

// District NAME for a snapshot that carries only the code (core withholds the street
// for non-participants, so district is all a core meeting may have). Session-cached —
// one fetch, however many cards. Resolves to '' when unknown: a raw district CODE must
// never reach the screen (districtLabel.js's rule).
let _districtsPromise = null
function useDistrictName(code) {
  const [name, setName] = useState('')
  useEffect(() => {
    if (!code) { setName(''); return }
    _districtsPromise ??= getDistricts().catch(() => [])
    let gone = false
    _districtsPromise.then(ds => {
      if (gone) return
      const d = (ds || []).find(x => x.code === code)
      setName(d?.name_ka || d?.name_en || '')
    })
    return () => { gone = true }
  }, [code])
  return name
}

export function MeetingStrip({ v, agents, show = {}, coops }) {
  const snap = v.property_snapshot || {}
  // The home-cockpit card used to say the bare fallback word ("Property") for every core
  // meeting whose street the server withholds — while the district was sitting unused in
  // the same snapshot (fixed 2026-08-15).
  const districtName = useDistrictName(snap.address ? null : snap.district_code)
  const st = MEETING_STATUS[v.status] || { variant: 'default' }
  const stLabel = MEETING_STATUS_KEYS[v.status] ? t(MEETING_STATUS_KEYS[v.status]) : v.status
  const schedAt = meetingScheduledAt(v)
  const schedText = schedAt
    ? fmtTbilisi(schedAt, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  const assigned = (v.agent_ids || []).map(id => agents.find(a => a.id === id)?.name).filter(Boolean)
  const specs = [
    snap.rooms_count != null ? tLog('desk_entry_rooms_abbrev', { rooms: snap.rooms_count }) : null,
    snap.area_sq_m != null ? tLog('desk_entry_area_abbrev', { area: snap.area_sq_m }) : null,
    snap.apt_floor != null ? (snap.total_floors != null ? tLog('desk_entry_floor_position', { floor: snap.apt_floor, total: snap.total_floors }) : tLog('desk_entry_floor_only', { floor: snap.apt_floor })) : null,
  ].filter(Boolean).join(' · ')
  const priceText = v.price != null ? `${Number(v.price).toLocaleString('en-US')} ${v.price_unit || ''}`.trim() : null
  const dealText = v.deal_type ? (DEAL_LABEL_KEYS[v.deal_type] ? t(DEAL_LABEL_KEYS[v.deal_type]) : v.deal_type) : null
  const propLine = [snap.address || districtName || t('desk_meetingcard_property_fallback'), priceText, dealText, specs].filter(Boolean).join(' · ')
  // The visit/purpose marker on the FACE (designer 2026-08-06): only three
  // purposes earn card space, because only they change what a manager does —
  // პირველი ნახვა, the visit count for a repeat, and გაფორმება. It rides this
  // strip (the row that already holds status and time), never a second pill on
  // the card header. The ordinal comes from getMeetingsFeed's one annotation
  // pass (__visit_no); a repeat with no computable ordinal shows its label.
  const purposeMark = purposeMarkOf(v)   // ONE implementation — see purposeMarkOf
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 px-1 text-xs text-muted-foreground border-t border-border/50 pt-1.5">
      {show.status && <Badge label={stLabel} variant={st.variant} />}
      {purposeMark && <span className="whitespace-nowrap"><Icon name="target" size={12} /> {purposeMark}</span>}
      {show.client && (v.__core
        // Core meeting: it owns relationships, not contacts (MEETINGS_MODEL §2),
        // so there is no client name here BY DESIGN. The two representatives are
        // what the agent actually needs — they are who you coordinate with, and
        // they are agency-visible without any reveal. The client's own contact,
        // when the viewer is entitled to it, arrives through the access request.
        ? (<>
          {v.client_representative?.name && (
            <span className="font-medium text-foreground truncate max-w-[45%]">
              <Icon name="handshake" size={12} /> {v.client_representative.name}
            </span>
          )}
          {v.property_representative?.name && (
            <span className="truncate max-w-[45%]">
              <Icon name={ROLE_ICON.agent} size={12} /> {v.property_representative.name}
            </span>
          )}
          {/* The second axis (§2.4): contact approval, never folded into status. */}
          {(v.access || []).filter(a => a.status === 'pending').length > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              <Icon name="bell" size={12} /> {t('desk_meeting_contact_pending')}
            </span>
          )}
        </>)
        : (<>
          <span className="font-medium text-foreground truncate max-w-[45%]">
            <Icon name="user" size={12} /> {v.client_name || v.client_phone || t('desk_meetingcard_fallback_title')}
          </span>
          {v.client_phone && v.client_name && <span><Icon name="phone" size={12} /> {fmtPhone(v.client_phone)}</span>}
        </>))}
      {show.time && <span>{schedText ? <>🕒 {schedText}</> : <span className="italic">{t('desk_meetingcard_no_time')}</span>}</span>}
      {show.time && <MeetingCountdown at={schedAt} status={v.status} />}
      {/* ⚠️ `show.terms === false` is the meetings feed opting out (2026-08-05): its state
          column now carries cooperation as a full row, and the card's rule is that the same
          fact never renders twice on one face. Default stays ON so the home cockpit's
          MeetingCard, which has no such column, is untouched. */}
      {show.terms !== false && <TermsToken coops={coops} partyIds={[
        v.property_representative?.party_id,
        ...(v.attendee_party_ids || []),
        v.client_representative?.party_id,
      ].filter(Boolean)} />}
      {/* One line the 2026-07-20 emoji→SVG sweep missed. These are assigned AGENTS, so they take
          the agent glyph rather than the generic person the raw 👤 rendered as — and an emoji
          ignores `currentColor`, so it could not be muted with the text beside it. */}
      {show.agents && assigned.length > 0 && (
        <span className="flex items-center gap-1"><Icon name={ROLE_ICON.agent} size={12} /> {assigned.join(', ')}</span>
      )}
      {show.property && (<>
        <span className="truncate max-w-full"><Icon name="house" size={12} /> {propLine}</span>
        {snap.source_url && (
          <a href={snap.source_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
            className="text-primary"><Icon name="link" size={12} /> {t('desk_meetingcard_listing_link')}</a>
        )}
      </>)}
    </div>
  )
}

// ── The medium tier's home (owner ruling 2026-08-06, see MeetingNudgesBanner).
export function meetingNudgeIndex(data) {
  const ix = new Map()
  for (const n of (data?.medium || [])) if (n.id) ix.set(n.id, n)
  return ix
}
// ⚠️ CORE ROWS ONLY, and the omission is deliberate. A backfilled meeting's feed row is
// the LEGACY twin (getMeetingsFeed drops the core one), and a legacy row opens the old
// ViewingForm — which has no reason chips and no follow-up date. Marking it would print a
// demand on a card whose only door cannot answer it, which is precisely what got the
// medium banner ruled off the main page. Seen in the harness: the legacy card drew
// "მიუთითეთ მიზეზი" and opened a form with nowhere to put one. See B338.
export const meetingNudgeOf = (ix, v) =>
  ix && v?.__core && v.core_meeting_id ? (ix.get(v.core_meeting_id) || null) : null
// Opening from a marked card must land ON the question the marker names — a card that
// says "add the reason" and opens a modal asking nothing is the defect this feature
// already shipped twice. Legacy rows open the old ViewingForm, which has no reason
// chips, so they must not be handed core-only flags.
export const armFromNudge = (v, n) => {
  if (!v?.__core) return v
  // The card's own red "ვადა გასულია" IS a nudge in everything but name, and it does not
  // arrive through the nudge index (that carries the medium tier only). Without this the
  // agent taps the loudest thing on the card and lands on a modal asking nothing —
  // the defect this feature has now shipped three times. Checked FIRST: an expired binder
  // outranks a missing reason, because one is a data gap and the other is money.
  if (binderExpired(v)) return { ...v, __askBinder: true }
  if (!n) return v
  if (n.why === 'binder_expired') return { ...v, __askBinder: true }
  return { ...v, __askReason: true, ...(n.why === 'missing_next_action' ? { __askNextAction: true } : {}) }
}

export function MeetingCard({ v, agents, thumbListing, coops, onOpen, nudge, onChanged = null }) {   // exported: the home cockpit's meetings section renders it too
  const snap = v.property_snapshot || {}
  const st = MEETING_STATUS[v.status] || { variant: 'default' }
  const stLabel = MEETING_STATUS_KEYS[v.status] ? t(MEETING_STATUS_KEYS[v.status]) : v.status
  const schedAt = meetingScheduledAt(v)
  const schedText = schedAt
    ? fmtTbilisi(schedAt, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  // Thumb: a listing already on the Desk that shares this property carries real photos + a
  // precise pin; merge its media onto the snapshot's address so ListingGallery can render a
  // photo (or its own geocoded map slide) instead of a blank box.
  const gallery = { ...(thumbListing || {}), address: snap.address || thumbListing?.address, images: thumbListing?.images || [] }

  // A-lite (ruling c, 2026-07-24): the compact glance-and-go FACE keeps time / client /
  // status / notes only; the property one-liner + assigned agents moved into the shared
  // MeetingStrip below the hairline. Face and strip must not overlap (see MeetingStrip).
  return (
    <div className={`${card} relative transition-all cursor-pointer hover:border-primary/40`} onClick={() => onOpen(armFromNudge(v, nudge))}>
      {/* keyboard door (Desk home audit 2026-10-21): the card click is mouse-only */}
      <button type="button" onClick={e => { e.stopPropagation(); onOpen(armFromNudge(v, nudge)) }} className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-20 focus:rounded focus:bg-primary focus:px-2 focus:py-1 focus:text-xs focus:text-primary-foreground">
        {t('desk_card_open')}
      </button>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg shrink-0"><Icon name="calendar" size={18} /></span>
          <p className="font-medium truncate">{v.client_name || v.client_phone || t('desk_meetingcard_fallback_title')}</p>
          <Badge label={stLabel} variant={st.variant} />
        </div>
      </div>
      {/* The gap, stated in full on the card that owns it. It sits under the title rather
          than beside the status badge on purpose: the badge says what the meeting IS, and
          this says what is MISSING — the same fact must never render as one chip.
          `shrink-0` on the dot, truncate the sentence: a nested-free row, so truncate is
          safe here (see the ⌂ ... trap in the handoff). */}
      {nudge && (
        <div className={`mt-1 flex items-center gap-1.5 text-[11px] ${NUDGE_TIER_STYLE.medium.text}`}>
          <span className={`shrink-0 ${NUDGE_TIER_STYLE.medium.dot}`}>●</span>
          <span className="truncate">{tOr(`desk_nudge_why_${nudge.why}`, nudge.why)}</span>
        </div>
      )}
      {/* The cockpit card has no state column, so the money says itself here. */}
      <BinderLine v={v} className="mt-1" />
      <div className="flex gap-2 mt-1">
        <ListingGallery listing={gallery} size="thumb" />
        <div className="flex-1 min-w-0 text-sm space-y-1">
          {/* Client */}
          {v.client_phone && <div className="text-muted-foreground text-xs"><Icon name="phone" /> {fmtPhone(v.client_phone)}</div>}
          {/* Meeting */}
          <div className="text-xs text-muted-foreground pt-1 flex flex-wrap items-center gap-x-2">
            {schedText ? <span>🕒 {schedText}</span> : <span className="italic">{t('desk_meetingcard_no_time')}</span>}
            <MeetingCountdown at={schedAt} status={v.status} />
          </div>
          {/* Notes */}
          {v.notes && <div className="text-xs text-muted-foreground truncate"><Icon name="sticky-note" /> {v.notes}</div>}
        </div>
      </div>
      <MeetingStrip v={v} agents={agents} coops={coops} show={{ agents: true, property: true }} />
      {/* The rail, bottom row, same place a listing card keeps its pills. */}
      <div className="mt-1.5 pt-1.5 border-t border-border/50 flex items-center">
        <MeetingRail v={v} agents={agents} onOpen={onOpen} onChanged={onChanged} />
      </div>
    </div>
  )
}

// statusFilter is CONTROLLED since 2026-07-22 (lifted to IntakeTab): the sidebar's
// Category→Meeting subtree and these pills are two surfaces over ONE state — the same
// contract as the search-box select vs the Category facet. Don't re-localize it.
export function MeetingsFeed({ viewings, agents, myAgentId, role, since, listingEntries, thumbsByProp = {}, coopsByProp = {}, onOpen, onChanged = null, statusFilter = 'active', onStatusChange = () => {}, districts = [], features = [], nudges, loading = false, loadError = null, onRetry = null }) {
  const setStatusFilter = onStatusChange
  // The medium tier renders HERE now, not in the banner (owner ruling 2026-08-06).
  const nudgeIx = useMemo(() => meetingNudgeIndex(nudges), [nudges])
  const nudgeOf = v => meetingNudgeOf(nudgeIx, v)
  // "Management" must match the server's projection gate (core_visibility MGMT_ROLES:
  // admin/manager/general_manager). Found 2026-07-20: 'manager' was missing here, so a
  // manager got server-granted data with the agent-grade UI (and saw NO meetings at all
  // in this feed when they had no roster row — the !isPrivileged branch filtered on a
  // null myAgentId).
  const isPrivileged = role === 'admin' || role === 'general_manager' || role === 'manager'
  // One representative loaded listing per property → its photos/pin for the card thumb,
  // TOPPED UP by `thumbsByProp` (property-keyed photo strips fetched by the Desk). The
  // loaded-rows lookup alone made the photo depend on the viewer's feed scope — see the
  // note on getPropertyThumbs in db/core.js.
  // Visibility (a plain agent sees only meetings they're on) + period, applied BEFORE the
  // status tabs so their counts reflect what this user can actually see.
  const scoped = useMemo(() => {
    let rows = viewings || []
    // ⚠️ agent_ids is the INVOLVED set (the file's own warning above: involved ≠ attending).
    // Filtering on it alone hid a meeting from the GUEST who is going to it — the exact
    // person the guest feature (0404) exists for. A guest attendee sees their meeting.
    if (!isPrivileged && myAgentId) rows = rows.filter(v =>
      (v.agent_ids || []).includes(myAgentId)
      || (v.attendees || []).some(a => a.agent_id === myAgentId))
    // The period pill filters on the APPOINTMENT date, not the data-entry date (fixed
    // 2026-08-15): filtering on created_at dropped a meeting booked last month and
    // scheduled today — the one row a period filter on an appointments feed must keep.
    // A row with no time falls back to created_at rather than vanishing.
    if (since) rows = rows.filter(v => ((meetingScheduledAt(v) || v.created_at || '')) >= since)
    return rows
  }, [viewings, isPrivileged, myAgentId, since])
  const visible = useMemo(() => {
    let rows = scoped
    // review 2026-09-23: legacy 'viewing' (unbackfilled old rows) is in neither list, so it
    // fell out of Active AND Finished. It aliases 'scheduled' — DealsTab's badge/QUICK treat
    // it the same — so filter on the aliased status. Display is untouched.
    const st = v => (v.status === 'viewing' ? 'scheduled' : v.status)
    if (statusFilter === 'active') rows = rows.filter(v => MEETING_ACTIVE.includes(st(v)))
    else if (statusFilter === 'finished') rows = rows.filter(v => MEETING_FINISHED.includes(st(v)))
    else if (statusFilter !== 'all') rows = rows.filter(v => st(v) === statusFilter)
    // Sorted by WHEN THE MEETING IS, newest first — an appointments feed answers "what is
    // on", not "what did I type recently". created_at is the timeless-row fallback only.
    const at = v => meetingScheduledAt(v) || v.created_at || ''
    rows = [...rows].sort((a, b) => at(b).localeCompare(at(a)))
    // 0690 — every visit stays a card (owner 2026-08-21: "previous meetings card is gone"
    // was the complaint the minute they were folded). What makes it ONE deal is the card
    // face: the closing visit carries the deal line (visits + participants), every earlier
    // visit of the same deal reads "revisited — outcome on the later visit". One trophy.
    const visitsByDeal = new Map()
    for (const v of rows) if (v.__core && v.deal_id) visitsByDeal.set(v.deal_id, (visitsByDeal.get(v.deal_id) || 0) + 1)
    return rows.map(v => (v.__core && v.deal_id ? { ...v, __deal_visits: visitsByDeal.get(v.deal_id) } : v))
  }, [scoped, statusFilter])

  // The status pill rows are GONE (owner 2026-07-24: "redundant now — we already have
  // categories"): the sidebar's Category→Meeting subtree is the one status surface, over
  // the same lifted statusFilter.
  //
  // ⚠️ …which is exactly why the medium tier needs a way back. Its markers live on card
  // FACES now, and most of the pile is `failed` — a FINISHED status, so the default
  // `active` view hides every one of them. Moving the tier off the banner and onto cards
  // nobody is looking at would have retired it, not relocated it. So: when this view is
  // hiding meetings that owe an answer, say so ONCE, with the count, and let the sentence
  // BE the control that reveals them. Not a nag — it appears only when the gap and the
  // filter actually disagree, and answering the last one removes it.
  const hiddenNudged = useMemo(() => {
    if (statusFilter === 'all') return 0
    const shown = new Set(visible.map(v => v.id))
    return scoped.filter(v => !shown.has(v.id) && nudgeOf(v)).length
  }, [scoped, visible, statusFilter, nudgeIx])  // eslint-disable-line react-hooks/exhaustive-deps



  return (
    <div>
      {hiddenNudged > 0 && (
        <button type="button" onClick={() => setStatusFilter('all')}
          className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground">
          <span className={`shrink-0 ${NUDGE_TIER_STYLE.medium.dot}`}>●</span>
          <span>{tLog('desk_nudge_hidden_by_view', { n: hiddenNudged })}</span>
        </button>
      )}
      {/* ⚠️ THREE SITUATIONS, THREE SENTENCES (fixed 2026-08-15). One string —
          "no meetings match these filters" — used to cover all of them, and it was wrong
          in two: the feed is fetched lazily when the Meeting category is first opened, so
          the FIRST PAINT of the tab always blamed filters for an empty screen; and an
          agent with no meetings at all was told to go and adjust filters that were
          innocent. Only the third case is about filtering, and there the honest thing is
          to say how many rows the filter is holding back and make the sentence undo it —
          the shape the hidden-nudged button above already uses. */}
      {visible.length === 0
        ? loading
          ? <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>
          // the feed did not load — never "no meetings yet" for that (owner 2026-10-18)
          : loadError
            ? <div className="text-sm text-destructive flex flex-wrap items-center gap-2">
                <span>{t('desk_meetings_load_failed')}</span>
                {onRetry && <button type="button" onClick={onRetry} className="underline underline-offset-2">{t('desk_retry')}</button>}
              </div>
          : scoped.length === 0
            ? <p className="text-sm text-muted-foreground">{t('desk_meetings_none_at_all')}</p>
            : <button type="button" onClick={() => setStatusFilter('all')}
                className="text-sm text-muted-foreground hover:text-foreground text-left">
                {tLog('desk_meetings_all_filtered', { n: scoped.length })}
              </button>
        : (
          /* `desk-grid-equal` replaces `items-start` (index.css §1b, owner 2026-08-05): every
             card in a row takes the row's height, so the grid reads as one edge instead of a
             ragged one. It is the ONLY grid carrying this today — the other card grids are
             unchanged, because equal heights buy a tidy edge with empty space under the
             shorter cards, and that is a per-surface call.
             ⚠️ A plain JS comment, NOT a braced JSX one: this sits inside a ternary branch,
             where a JSX comment would be a SECOND child and the file stops compiling. (And
             do not quote a closing comment marker in here either — it ends this comment.) */
          <MeetingCardGrid viewings={visible} agents={agents} listingEntries={listingEntries} thumbsByProp={thumbsByProp}
            coopsByProp={coopsByProp} districts={districts} features={features} nudges={nudges} onOpen={onOpen} onChanged={onChanged} />
        )}
    </div>
  )
}


// ── The Desk meeting card, as a grid (2026-09-06) ─────────────────────────────────────────
// Extracted from MeetingsFeed so the Meetings TAB (DealsTab) draws the SAME card — owner: "the
// exact design of the cards we have in the Desk". Owns everything the card needs beyond the
// row itself: the property-keyed thumb lookup, the per-ad restrictions/clears fetch, and the
// listing-card synthesis (groupOf). The parent keeps filtering, sorting and the empty states.
export function MeetingCardGrid({ viewings, agents, listingEntries = [], thumbsByProp = {}, coopsByProp = {}, districts = [], features = [], nudges = null, onOpen, onChanged = null }) {
  const nudgeIx = useMemo(() => meetingNudgeIndex(nudges), [nudges])
  const nudgeOf = v => meetingNudgeOf(nudgeIx, v)
  const thumbByProp = useMemo(() => {
    const m = new Map()
    for (const l of listingEntries || []) if (l.property_id && !m.has(l.property_id)) m.set(l.property_id, l)
    for (const [pid, images] of Object.entries(thumbsByProp || {})) {
      if (!images?.length) continue
      const have = m.get(pid)
      if (!have) m.set(pid, { property_id: pid, images })
      else if (!have.images?.length) m.set(pid, { ...have, images })
    }
    return m
  }, [listingEntries, thumbsByProp])
  // ── Restrictions for the meetings on screen (B312, 2026-08-05) ──────────────────────────
  // ⚠️ This feed fetches its OWN, rather than taking IntakeTab's `restrByListing`. That index
  // is filled from `visibleListingIds` — the PROPERTY feed's page — and a meetings page shares
  // almost no rows with it, so handing it over would answer for a few cards and go quiet on
  // the rest. On this column quiet is a claim ("we asked, there are none"), which is precisely
  // the doorstep failure the row exists to prevent.
  //
  // The ad is resolved per meeting: a core meeting carries `listing_id` (migration 0357);
  // otherwise a loaded listing on the same property stands in. A meeting with neither stays
  // UNKNOWN and says so — it does not borrow another flat's answer.
  //
  // `null` is the fetched-empty marker, the same convention IntakeTab uses: absence of a key
  // means NOT ASKED, a null value means asked and nothing came back. Collapsing those two is
  // what turns "we don't know" into "there are none".
  const listingIdOf = v => v.listing_id || thumbByProp.get(v.property_snapshot?.core_property_id)?.listing_id || null
  const [restrByListing, setRestrByListing] = useState({})
  const [clearByListing, setClearByListing] = useState({})
  const restrSeen = useRef(new Set())
  const wantedIds = useMemo(
    () => [...new Set((viewings || []).map(listingIdOf).filter(Boolean))],
    [viewings, thumbByProp])
  useEffect(() => {
    const want = wantedIds.filter(id => !restrSeen.current.has(id))
    if (!want.length) return
    for (const id of want) restrSeen.current.add(id)
    const chunks = []                                   // 80 is the endpoint's own hard cap
    for (let i = 0; i < want.length; i += 80) chunks.push(want.slice(i, i + 80))
    Promise.all(chunks.map(c => getListingTermsBatch(c))).then(batches => {
      const m = {}, mc = {}
      for (const id of want) m[id] = null                // fetched-empty, deterministic
      for (const b of batches) {
        for (const p of (b.policies || [])) {
          // Only RESTRICTING stances — the same filter the card chip uses. `allowed` and
          // `preferred` are a different question and belong to a different row.
          if (p.stance !== 'not_allowed' && p.stance !== 'only') continue
          const e = (m[p.listing_id] ||= { subjects: [] })
          const src = (p.source_kind ?? 'agent') === 'agent' ? 'agent' : 'scrape'
          const prev = e.subjects.find(s => s.code === p.subject)
          if (!prev) e.subjects.push({ code: p.subject, stance: p.stance, src })
          else if (prev.src === 'scrape' && src === 'agent') { prev.stance = p.stance; prev.src = 'agent' }
        }
        for (const c of (b.clears || [])) if (c?.listing_id) mc[c.listing_id] = { cleared: true }
        for (const h of (b.hidden?.clears || [])) if (h?.listing_id && !mc[h.listing_id]) mc[h.listing_id] = { cleared: true, hidden: true }
      }
      setRestrByListing(prev => ({ ...prev, ...m }))
      setClearByListing(prev => ({ ...prev, ...mc }))
    }).catch(() => {
      // A failed read must leave the row UNKNOWN, never "none" — AND retryable: the ids
      // were optimistically marked seen above, so without this un-mark a single failed
      // batch left every one of them "already asked" for the rest of the session
      // (fixed 2026-08-15).
      for (const id of want) restrSeen.current.delete(id)
    })
  }, [wantedIds])
  // ── Card-morph ruling A (owner 2026-07-24): verbatim listing card + category strip ──
  // The feed renders the SAME HistoryGroup card every other Desk surface uses (the Leads
  // pattern, zero-migration baseline; C = morph-slot hybrid stays the eventual target),
  // with the meeting facts as a strip BELOW the card. The card face comes from a loaded
  // listing on the same property when one exists (real photos + pin); otherwise a pseudo-
  // listing is synthesized from the viewing's own property_snapshot — same trick LeadsFeed
  // uses, so a meeting whose ad expired still renders a full card, not a hole.
  const DEAL_TO_INTENT = { trade: 'sale', lease: 'rent' }
  const groupOf = v => {
    const snap = v.property_snapshot || {}
    const tl = thumbByProp.get(snap.core_property_id)
    // A REAL loaded listing (it has an id) is the richer face and wins whole. A
    // photo-only entry from getPropertyThumbs carries no facts, so it contributes
    // just its images to the snapshot-built pseudo-listing below.
    const listing = tl?.listing_id ? tl : {
      listing_id: null, address: snap.address || null, rooms: snap.rooms_count ?? null,
      floor: snap.apt_floor ?? null, floors_total: snap.total_floors ?? null,
      area_sqm: snap.area_sq_m ?? null, price: v.price ?? null, currency: v.price_unit || null,
      intent: DEAL_TO_INTENT[v.deal_type] || null, source: snap.source || null,
      source_url: snap.source_url || null, status: 'active', images: tl?.images || [],
      // Core meetings carry DISTRICT, never a street address (the server withholds
      // it — a meeting can involve a property the viewer doesn't represent). The
      // card resolves the code to a localized name via its `districts` prop, so
      // the row reads "Vake" rather than as a property with no location at all.
      district_code: snap.district_code || null,
    }
    return { key: `meet:${v.id}`, items: [{
      id: `viewing:${v.id}`, proposal_kind: 'scraped_listing_readonly',
      status: 'active', created_at: v.created_at || null, listing, proposed: {}, result: {},
    }] }
  }

  return (
      <div className="desk-grid-equal grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
        {viewings.map(v => (
          <div key={v.id} className="relative cursor-pointer" onClick={() => onOpen(armFromNudge(v, nudgeOf(v)))}>
            {/* Category strip (ruling A → A-lite → increment 4): the SHARED MeetingStrip
                now rides INSIDE the card via the skeleton's strip slot — the card owns
                position, this feed supplies content (status · client · time · agents).
                Clicking anywhere opens the meeting. */}
            <HistoryGroup group={groupOf(v)} districts={districts} features={features}
              onCardClick={() => onOpen(armFromNudge(v, nudgeOf(v)))} expanded={false} shade={0} onToggle={() => {}}
              gallerySize="thumb"
              /* The meeting's own state column (2026-08-05). */
              /* The bottom rail: contact + outcome pills, where a listing card keeps its
                 phone/share/link pills. HistoryGroup renders the rail for `strip` alone. */
              strip={<MeetingRail v={v} agents={agents} onOpen={onOpen} onChanged={onChanged} />}
              stateSlot={<MeetingStateBlock v={v} agents={agents} features={features} nudge={nudgeOf(v)}
                coops={coopsByProp?.[v.property_snapshot?.core_property_id]}
                restr={restrByListing[listingIdOf(v)]} clear={clearByListing[listingIdOf(v)]} />}
              /* ⚠️ NO MeetingStrip on this surface (2026-08-05): status, client, time, agents
                 and terms live in the column above, and its property line is the card face
                 verbatim. The `strip` prop above is the RAIL slot, not that strip — it holds
                 the two pills and nothing textual. MeetingStrip stays for the cockpit card. */ />
          </div>
        ))}
      </div>
  )
}

// ── Meeting follow-up nudges (0427) ──────────────────────────────────────────
// The B111 nudge shape, one object over: "human behavior follows reminders far
// more reliably than good intentions." Three tiers from /api/core/meetings/nudges,
// computed live server-side — resolving the meeting IS dismissing the row, so
// there is no read-state and nothing to swipe away:
//   critical (red)   — the appointment passed and the record still says scheduled
//   high (orange)    — pending whose follow-up date passed, or pending gone stale
//   medium (amber)   — a status missing its reason (recent window only)
// NUDGE, NEVER A GATE: it renders nothing when nothing is owed, and every row is
// a deep link into the meeting where 1–2 taps resolve it. Rendered above the Desk
// entries views (beside OpenTouchNudge); the bell badges critical+high only.
// Designer system 2026-08-06 (variant 1a): ONE bordered box, grouped BY TIER —
// critical rows always drawn in full (1–3 in practice), high collapsed behind
// its count line, medium a single muted SENTENCE (a count and a statement that
// the pile drains — never a list; "the tail can never make the top scroll").
// Rows carry date · status · why only (a meeting owns no contact/address here).
export const NUDGE_TIER_STYLE = {
  critical: { box: 'border-red-500/40 bg-red-500/10', text: 'text-red-700 dark:text-red-400', dot: 'text-red-500' },
  high:     { box: 'border-orange-500/40 bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', dot: 'text-orange-500' },
  medium:   { box: 'border-amber-500/40 bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', dot: 'text-amber-500' },
}
// The glyph names the STATE, not the object (designer ruling): calendar = an
// appointment that passed, hourglass = a pending row whose date/patience ran
// out, alert-circle = a missing reason. Shared with the bell and the timeline
// rail so a manager moving between surfaces reads one language.
export const NUDGE_WHY_ICON = {
  meeting_not_updated: 'calendar',
  next_action_date_passed: 'hourglass',
  pending_stale: 'hourglass',
  missing_reason: 'alert-circle',
  missing_next_action: 'hourglass',
  // 0434. The glyph names the STATE, and the state here is money on a clock that ran out.
  binder_expired: 'alert-triangle',
  // 0690. The flat went to another client; the visit was cancelled under the agent.
  property_gone: 'ban',
}
// ── The binder, said on a face (0434) ───────────────────────────────────────────────────
// ⚠️ Stored and unseen is the same failure as the medium nudge: the figures existed and no
// surface drew them, so the only way to learn how much money a deal is holding was to open
// the modal and press the status chip. ONE renderer, shared by the card's state column and
// the modal's facts zone, so the two can never disagree about a number.
// Expiry is stated only when it has PASSED — a live binder's date is detail, an expired
// one is the single most urgent fact on the card, and it is the same sentence the critical
// nudge uses so a manager moving between surfaces reads one language.
// ONE definition of "this binder's time is up", so the red text on the card, the arming of
// the panel and the server's nudge cannot drift into disagreeing about the same date.
// ⚠️ Compared in TBILISI date terms, not UTC and not the device's (owner ruling 2026-08-12 —
// this line used the DEVICE's local date until then, so an agent's phone zone decided when a
// binder read as expired). The server's nudge uses a UTC `today`, so for four hours after
// midnight in Tbilisi the card can call a binder expired before the nudge does. The card
// leading is the harmless direction — see B339.
// `binder_close_date` is the LEGACY row's equivalent field (see BinderLine's note on the two
// models). It means "expected close" rather than "the binder stops holding", which is a
// weaker claim — so it is read, never treated as authoritative for expiry.
// One day formatter for the plain YYYY-MM-DD dates this file renders (binder deadline,
// follow-up date). They printed RAW next to localized datetimes on the same card —
// "2026-08-20" beside "Aug 15, 15:30" (fixed 2026-08-15). Noon anchors the parse so no
// timezone can shift the day.
// Noon anchors the parse and fmtTbilisi renders in the agency's zone, so no device
// offset can shift the day either way — the same rule the datetime rows follow.
export const fmtDay = d => {
  if (!d) return ''
  const iso = `${String(d).slice(0, 10)}T12:00:00`
  return fmtTbilisi(iso, { day: 'numeric', month: 'short' }) || String(d)
}

export const binderDate = v => v?.binder_expires_on || null
export const binderExpired = v =>
  !!(binderDate(v) && binderDate(v) < tbilisiParts(new Date().toISOString()).date)

export function BinderLine({ v, className = '' }) {
  if (v?.binder_amount == null) return null
  const expired = binderExpired(v)
  // ⚠️ TWO BINDER MODELS EXIST, and 0434 is the one that created the second. The legacy
  // scheduler `viewings` row has carried binder_amount / binder_currency / binder_paid_date /
  // binder_close_date / binder_holder since long before this — DealsTab still writes them —
  // and core now has its own columns plus a share TABLE. Nothing reconciles the two, so
  // which is canonical is an open decision (B340).
  // What this renderer can do without that decision is REFUSE TO GO BLANK on a legacy row:
  // the fields overlap enough to read, so a legacy binder shows its figure and its date
  // instead of nothing. It is a read-side adapter and deliberately not a write path —
  // guessing a merge is how two records become three.
  const shares = v.binder_shares?.length ? v.binder_shares.filter(s => Number(s.percent) > 0)
    : v.binder_holder ? [{ holder_code: v.binder_holder, percent: 100 }] : []
  return (
    <div className={`flex flex-wrap items-baseline gap-x-1.5 text-[11px] ${className}`}>
      <span className="font-medium text-foreground">
        {t('meeting_binder_short')} {Number(v.binder_amount).toLocaleString()} {v.binder_currency || ''}
      </span>
      {v.binder_expires_on && (
        <span className={expired ? NUDGE_TIER_STYLE.critical.text : 'text-muted-foreground'}>
          · {fmtDay(v.binder_expires_on)}{expired ? ` · ${t('meeting_binder_summary_expired')}` : ''}
        </span>
      )}
      {/* The split, only when it is one — "owner 100%" is the default reading of a binder
          and restating it is noise on a face that is already dense. */}
      {shares.length > 1 && (
        <span className="text-muted-foreground">
          · {shares.map(s => `${tOr(`meeting_binder_holder_${s.holder_code}`, s.holder_code)} ${Number(s.percent)}%`).join(' / ')}
        </span>
      )}
    </div>
  )
}

const nudgeWhen = at => at ? fmtTbilisi(at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

// One nudge row: stacked date/status + why on a phone, one line with the
// explicit გახსნა button at ≥sm (the row stays clickable everywhere — nothing
// depends on finding the button; on a phone the whole 44px row is the target).
function NudgeRow({ n, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen?.(n)}
      className="w-full text-left flex items-start sm:items-center gap-2 text-xs min-w-0 rounded px-1 py-1 hover:bg-accent/50">
      <span className={`shrink-0 ${NUDGE_TIER_STYLE[n.tier].dot}`}>●</span>
      <span className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-2">
        <span className="shrink-0">
          {/* ⚠️ THE DATE A NUDGE SHOWS MUST BE THE DATE IT IS ABOUT. Every other tier is
              about the appointment, so scheduled_at is right; an expired binder is about
              a DEADLINE, and printing the viewing date next to "ბეს ვადა გავიდა" invites
              the agent to check the wrong thing. The amount rides along because the first
              question anyone asks about an expired binder is how much is sitting there. */}
          <span className="text-foreground">
            {n.why === 'binder_expired' && n.binder_expires_on ? fmtDay(n.binder_expires_on) : nudgeWhen(n.scheduled_at)}
          </span>
          {n.why === 'binder_expired' && n.binder_amount != null
            ? <span className="text-foreground"> · {Number(n.binder_amount).toLocaleString()} {n.binder_currency || ''}</span>
            : <span className="text-muted-foreground"> · {t(MEETING_STATUS_KEYS[n.status] || 'desk_meeting_status_scheduled')}</span>}
        </span>
        {/* WHICH meeting (owner-caught 2026-08-06: "მიზეზი რისი?" — a nudge with no
            subject is a question nobody can answer). Address for the people on the
            meeting; the server scopes rows to exactly them. */}
        {(n.property?.address || n.property?.district_code) && (
          <span className="min-w-0 truncate text-foreground">
            {n.property.address || n.property.district_code}
          </span>
        )}
        <span className="min-w-0 truncate text-muted-foreground">{tOr(`desk_nudge_why_${n.why}`, n.why)}
          {n.waiting_on ? ` · ${tOr(`desk_nudge_waiting_${n.waiting_on}`, n.waiting_on)}` : ''}
          {n.next_action_date ? ` · ${fmtDay(n.next_action_date)}` : ''}
        </span>
      </span>
      <span className="ml-auto shrink-0 self-center text-muted-foreground sm:hidden">›</span>
      <span className="ml-auto shrink-0 self-center hidden sm:inline text-xs rounded border border-border px-1.5 py-px hover:bg-accent"
        onClick={e => { e.stopPropagation(); onOpen?.(n) }}>{t('desk_nudge_open')}</span>
    </button>
  )
}

export function MeetingNudgesBanner({ data: dataProp, onOpenMeeting, onLoaded, onRetry }) {
  // Fed by the parent when it already fetched (IntakeTab loads nudges for the
  // bell badge); self-fetching otherwise, so the component works on any surface.
  const [fetched, setFetched] = useState(null)
  const data = dataProp !== undefined ? dataProp : fetched
  const [highOpen, setHighOpen] = useState(false)
  useEffect(() => {
    if (dataProp !== undefined) return
    getMeetingNudges().then(d => { setFetched(d); onLoaded?.(d) }).catch(() => setFetched({ failed: true }))
  }, [dataProp === undefined])  // eslint-disable-line react-hooks/exhaustive-deps
  // a failed read says so (audit 2026-10-21) - silence here means "nothing overdue", and that was not known
  if (data?.failed) return (
    <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/5 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400" role="status">
      <Icon name="alert-triangle" /> {t('desk_nudge_failed')}
      {onRetry && <>{' '}<button type="button" className="underline" onClick={onRetry}>{t('desk_home_retry')}</button></>}
    </div>
  )
  const crit = data?.critical || [], high = data?.high || []
  // Medium alone renders NOTHING here — a banner whose only content is a number the
  // agent cannot act on is the thing that got this surface ruled off the main page.
  if (!crit.length && !high.length) return null
  const worst = crit.length ? 'critical' : 'high'
  return (
    <div className={`mb-3 rounded border px-3 py-2 ${NUDGE_TIER_STYLE[worst].box}`}>
      {/* Header = the worst tier's own sentence, not a generic count. Critical is
          never collapsible: an unknown outcome is what this surface exists to chase. */}
      {crit.length > 0 && (
        <div className={`text-xs font-medium ${NUDGE_TIER_STYLE.critical.text}`}>
          <Icon name="alert-triangle" /> {tLog('desk_nudge_tier_critical', { n: crit.length })}
        </div>
      )}
      {crit.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-0.5 border-b border-border/40 pb-1.5 last:border-0 last:pb-0">
          {crit.map(n => <NudgeRow key={n.id} n={n} onOpen={onOpenMeeting} />)}
        </div>
      )}
      {high.length > 0 && (
        <div className={crit.length ? 'mt-1.5' : ''}>
          <button type="button" className={`text-xs font-medium ${NUDGE_TIER_STYLE.high.text}`}
            aria-expanded={highOpen} onClick={() => setHighOpen(o => !o)}>
            {!crit.length && <><Icon name="alert-triangle" />{' '}</>}
            {tLog('desk_nudge_tier_high', { n: high.length })} {highOpen ? '▲' : '▼'}
          </button>
          {highOpen && (
            <div className="mt-1 flex flex-col gap-0.5">
              {high.map(n => <NudgeRow key={n.id} n={n} onOpen={onOpenMeeting} />)}
            </div>
          )}
        </div>
      )}
      {/* ⚠️ MEDIUM IS NOT HERE, and that is the ruling (owner, 2026-08-06, after meeting
          it live as lukab: "if its non actionable let it show in cards face — no need to
          put anything in main page that cant be understood and actionable"). It shipped as
          a muted sentence and read as a nag with no way in. The banner is now CRITICAL and
          HIGH only: both name an appointment that already went wrong, and both open.
          A missing reason still gets chased — on the FACE of the meeting card it belongs
          to (`meetingNudgeIndex` → `MeetingCard`), where the subject is already on screen
          and one tap opens it. That is a better surface for the tier, not a demotion:
          the count never told anyone WHICH meeting. It also keeps the bell, which is a
          list of things to work rather than a headline. */}
    </div>
  )
}
